import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DatePicker from "@/components/DatePicker";
import AiWizard from "@/components/AiWizard";
import ScheduleGrid from "@/components/ScheduleGrid";
import ApplyTemplateModal from "@/components/ApplyTemplateModal";
import { Plus, Trash2, ArrowLeft, Save, Send, AlertTriangle, Sparkles, LayoutGrid, Table as TableIcon, LayoutTemplate } from "lucide-react";

const SHIFT_TYPES = ["opening", "mid", "closing", "custom"];
const DEPARTMENTS = ["All", "Floor", "Cashier", "Stock", "Security", "Cleaning", "Management"];

function startOfWeekMonday(d = new Date()) {
  const day = d.getDay();
  const diff = (day + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - diff);
  return monday.toISOString().slice(0, 10);
}

export default function ScheduleBuilder() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { user } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeekMonday());
  const [department, setDepartment] = useState("All");
  const [comments, setComments] = useState("");
  const [shifts, setShifts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [showApplyTpl, setShowApplyTpl] = useState(false);
  const [view, setView] = useState("grid"); // grid | table

  useEffect(() => {
    if (["admin", "gm", "manager"].includes(user?.role)) {
      api.get("/employees").then(({ data }) => setEmployees(data)).catch((e) => console.warn("Load employees failed", e));
    }
  }, [user]);

  const loadSchedule = useCallback(() => {
    if (isNew) return;
    api.get(`/schedules/${id}`).then(({ data }) => {
      setSchedule(data);
      setWeekStart(data.week_start);
      setDepartment(data.department || "All");
      setComments(data.manager_comments || "");
      setShifts(data.shifts || []);
    });
  }, [id, isNew]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const editable = isNew || (schedule && ["draft", "changes_requested"].includes(schedule.status));

  const addShift = () => setShifts([...shifts, {
    employee_id: "", employee_name: "", date: weekStart,
    start_time: "10:00", end_time: "18:00", break_minutes: 30,
    role: "Floor", department: department === "All" ? "Floor" : department,
    shift_type: "mid", notes: "",
  }]);

  const updateShift = (idx, patch) => {
    const next = [...shifts];
    next[idx] = { ...next[idx], ...patch };
    if (patch.employee_id) {
      const emp = employees.find((e) => e.id === patch.employee_id);
      if (emp) next[idx].employee_name = emp.name;
    }
    setShifts(next);
  };
  const removeShift = (idx) => setShifts(shifts.filter((_, i) => i !== idx));
  const duplicateShift = (idx) => setShifts([...shifts, { ...shifts[idx] }]);

  const summary = useMemo(() => {
    const hours = {};
    for (const s of shifts) {
      try {
        const [h1, m1] = s.start_time.split(":").map(Number);
        const [h2, m2] = s.end_time.split(":").map(Number);
        const start = h1 * 60 + m1;
        let end = h2 * 60 + m2;
        if (end <= start) end += 24 * 60; // crosses midnight
        let mins = end - start - (Number(s.break_minutes) || 0);
        if (mins < 0) mins = 0;
        hours[s.employee_id] = (hours[s.employee_id] || 0) + mins / 60;
      } catch (_) {}
    }
    return {
      total_hours: Object.values(hours).reduce((a, b) => a + b, 0),
      by_emp: hours,
      shift_count: shifts.length,
      employee_count: Object.keys(hours).length,
    };
  }, [shifts]);

  const warnings = useMemo(() => {
    const w = [];
    shifts.forEach((s, i) => {
      if (!s.employee_id) w.push(`Shift ${i+1}: no employee assigned`);
      if (!s.start_time || !s.end_time) w.push(`Shift ${i+1}: missing time`);
      else if (s.start_time === s.end_time) w.push(`Shift ${i+1}: start and end time are the same`);
    });
    Object.entries(summary.by_emp).forEach(([eid, h]) => {
      if (h > 40) {
        const name = employees.find((e) => e.id === eid)?.name || eid;
        w.push(`${name} scheduled ${h.toFixed(1)}h (>40h)`);
      }
    });
    return w;
  }, [shifts, summary, employees]);

  const saveDraft = async () => {
    setBusy(true);
    try {
      const payload = { week_start: weekStart, department, manager_comments: comments, shifts };
      if (isNew) {
        const { data } = await api.post("/schedules", payload);
        toast.success(`Draft saved: ${data.schedule_number}`);
        nav(`/schedules/${data.id}`);
      } else {
        await api.patch(`/schedules/${id}`, { manager_comments: comments, shifts });
        toast.success("Draft updated");
        loadSchedule();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async (submitComments) => {
    setBusy(true);
    try {
      if (isNew || !schedule) {
        const { data } = await api.post("/schedules", { week_start: weekStart, department, manager_comments: comments, shifts });
        await api.post(`/schedules/${data.id}/submit`, { comments: submitComments });
        toast.success("Schedule submitted for approval");
        nav(`/schedules/${data.id}`);
      } else {
        // ensure latest state is persisted first
        await api.patch(`/schedules/${id}`, { manager_comments: comments, shifts });
        await api.post(`/schedules/${id}/submit`, { comments: submitComments });
        toast.success("Schedule submitted for approval");
        loadSchedule();
        setShowSubmit(false);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="schedule-builder">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      <div>
        <div className="label-caps">Workforce</div>
        <h1 className="font-heading text-3xl sm:text-4xl">
          {isNew ? "New Weekly Schedule" : `Schedule ${schedule?.schedule_number || ""}`}
        </h1>
        {schedule && (
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">
            Status: <b>{schedule.status.replace(/_/g, " ")}</b>
            {schedule.changes_comments && <div className="mt-2 p-3 rounded-lg border border-[color:var(--warning)] bg-[color:var(--warning)]/10 text-sm">Changes requested: {schedule.changes_comments}</div>}
            {schedule.rejection_reason && <div className="mt-2 p-3 rounded-lg border border-[color:var(--error)] bg-[color:var(--error)]/10 text-sm">Rejected: {schedule.rejection_reason}</div>}
          </div>
        )}
      </div>

      <div className="hos-card p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-caps">Week starting (Mon)</label>
          <DatePicker value={weekStart} onChange={setWeekStart} disabled={!editable} testId="week-start-input"/>
        </div>
        <div>
          <label className="label-caps">Department</label>
          <select className="hos-input" value={department} onChange={(e) => setDepartment(e.target.value)} disabled={!editable} data-testid="dept-select">
            {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Manager Comments</label>
          <input className="hos-input" value={comments} onChange={(e) => setComments(e.target.value)} disabled={!editable} data-testid="manager-comments-input"/>
        </div>
      </div>

      <div className="hos-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-2xl">Shifts ({shifts.length})</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-[color:var(--border)] overflow-hidden" data-testid="view-toggle">
              <button
                onClick={() => setView("grid")}
                className={`px-3 py-1.5 text-xs flex items-center gap-1 ${view === "grid" ? "bg-[color:var(--gold)] text-black" : "text-[color:var(--text-secondary)]"}`}
                data-testid="view-grid-btn"
              >
                <LayoutGrid size={14}/> Grid
              </button>
              <button
                onClick={() => setView("table")}
                className={`px-3 py-1.5 text-xs flex items-center gap-1 ${view === "table" ? "bg-[color:var(--gold)] text-black" : "text-[color:var(--text-secondary)]"}`}
                data-testid="view-table-btn"
              >
                <TableIcon size={14}/> Table
              </button>
            </div>
            {editable && (
              <>
                <button className="hos-btn-outline flex items-center gap-2" onClick={() => setShowApplyTpl(true)} data-testid="apply-template-btn"><LayoutTemplate size={16}/> Apply Template</button>
                <button className="hos-btn-outline flex items-center gap-2" onClick={() => setShowAi(true)} data-testid="ai-open-btn"><Sparkles size={16}/> Generate with AI</button>
                <button className="hos-btn-outline flex items-center gap-2" onClick={addShift} data-testid="add-shift-btn"><Plus size={16}/> Add Shift</button>
              </>
            )}
          </div>
        </div>
        {view === "grid" ? (
          <ScheduleGrid
            shifts={shifts}
            employees={employees}
            weekStart={weekStart}
            editable={!!editable}
            department={department}
            onChange={setShifts}
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left label-caps"><tr>
              <th className="p-2">Employee</th><th>Date</th><th>Start</th><th>End</th><th>Break (min)</th><th>Role</th><th>Dept</th><th>Type</th><th>Notes</th><th></th>
            </tr></thead>
            <tbody>
              {shifts.map((s, i) => (
                <tr key={i} className="border-t border-[color:var(--border)]">
                  <td className="p-2">
                    <select className="hos-input py-1 min-w-[160px]" value={s.employee_id} onChange={(e) => updateShift(i, { employee_id: e.target.value })} disabled={!editable} data-testid={`shift-${i}-employee`}>
                      <option value="">— select —</option>
                      {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </td>
                  <td><input type="date" className="hos-input py-1" value={s.date} onChange={(e) => updateShift(i, { date: e.target.value })} disabled={!editable} data-testid={`shift-${i}-date`}/></td>
                  <td><input type="time" className="hos-input py-1" value={s.start_time} onChange={(e) => updateShift(i, { start_time: e.target.value })} disabled={!editable} data-testid={`shift-${i}-start`}/></td>
                  <td><input type="time" className="hos-input py-1" value={s.end_time} onChange={(e) => updateShift(i, { end_time: e.target.value })} disabled={!editable} data-testid={`shift-${i}-end`}/></td>
                  <td><input type="number" className="hos-input py-1 max-w-[80px]" value={s.break_minutes} onChange={(e) => updateShift(i, { break_minutes: Number(e.target.value) })} disabled={!editable}/></td>
                  <td><input className="hos-input py-1 max-w-[110px]" value={s.role || ""} onChange={(e) => updateShift(i, { role: e.target.value })} disabled={!editable}/></td>
                  <td><input className="hos-input py-1 max-w-[110px]" value={s.department || ""} onChange={(e) => updateShift(i, { department: e.target.value })} disabled={!editable}/></td>
                  <td>
                    <select className="hos-input py-1" value={s.shift_type || "mid"} onChange={(e) => updateShift(i, { shift_type: e.target.value })} disabled={!editable}>
                      {SHIFT_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </td>
                  <td><input className="hos-input py-1" value={s.notes || ""} onChange={(e) => updateShift(i, { notes: e.target.value })} disabled={!editable}/></td>
                  <td>
                    {editable && (
                      <div className="flex gap-1">
                        <button className="p-1 text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]" onClick={() => duplicateShift(i)} title="Duplicate">+</button>
                        <button className="p-1 text-[color:var(--error)]" onClick={() => removeShift(i)} data-testid={`shift-${i}-remove`}><Trash2 size={14}/></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {shifts.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center text-[color:var(--text-secondary)]">No shifts yet — add one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">Summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Info k="Shifts" v={summary.shift_count}/>
          <Info k="Employees" v={summary.employee_count}/>
          <Info k="Total hours" v={summary.total_hours.toFixed(1)}/>
          <Info k="Warnings" v={warnings.length}/>
        </div>
        {warnings.length > 0 && (
          <div className="mt-4 p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-lg text-sm">
            <div className="flex items-center gap-2 text-[color:var(--warning)] font-semibold mb-2"><AlertTriangle size={16}/> Warnings</div>
            <ul className="list-disc pl-5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
      </div>

      {editable && (
        <div className="flex flex-wrap gap-3 justify-end">
          <button className="hos-btn-outline flex items-center gap-2" onClick={saveDraft} disabled={busy} data-testid="save-draft-btn"><Save size={16}/> Save Draft</button>
          <button className="hos-btn-primary flex items-center gap-2" onClick={() => setShowSubmit(true)} disabled={busy || shifts.length === 0} data-testid="submit-schedule-btn">
            <Send size={16}/> Submit for Approval
          </button>
        </div>
      )}

      {showSubmit && (
        <SubmitModal
          summary={summary} warnings={warnings} weekStart={weekStart}
          onClose={() => setShowSubmit(false)}
          onSubmit={submit} busy={busy}
        />
      )}
      {showAi && (
        <AiWizard
          employees={employees}
          defaultWeek={weekStart}
          onClose={() => setShowAi(false)}
          onApply={(newShifts, wk) => { setWeekStart(wk); setShifts(newShifts); }}
        />
      )}
      {showApplyTpl && (
        <ApplyTemplateModal
          employees={employees}
          weekStart={weekStart}
          department={department}
          onClose={() => setShowApplyTpl(false)}
          onApply={(newShifts) => setShifts([...shifts, ...newShifts])}
        />
      )}
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="label-caps">{k}</div>
      <div className="text-lg">{v}</div>
    </div>
  );
}

function SubmitModal({ summary, warnings, weekStart, onClose, onSubmit, busy }) {
  const [comments, setComments] = useState("");
  const [ack, setAck] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-2xl mb-4">Submit Schedule for Approval</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Info k="Week starting" v={weekStart}/>
          <Info k="Shifts" v={summary.shift_count}/>
          <Info k="Employees" v={summary.employee_count}/>
          <Info k="Total hours" v={summary.total_hours.toFixed(1)}/>
        </div>
        {warnings.length > 0 && (
          <div className="mt-4 p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-lg text-sm">
            <div className="text-[color:var(--warning)] font-semibold mb-1">{warnings.length} unresolved warning(s):</div>
            <ul className="list-disc pl-5">{warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
        <textarea className="hos-input min-h-[80px] mt-4" placeholder="Optional message to the General Manager…" value={comments} onChange={(e) => setComments(e.target.value)} data-testid="submit-comments"/>
        <label className="flex items-start gap-2 mt-3 text-sm">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="submit-ack" className="mt-1"/>
          <span>I confirm that I have reviewed this schedule and am submitting it to the General Manager for approval.</span>
        </label>
        <div className="flex justify-end gap-3 mt-4">
          <button className="hos-btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="hos-btn-primary" onClick={() => onSubmit(comments)} disabled={!ack || busy} data-testid="submit-confirm-btn">Submit</button>
        </div>
      </div>
    </div>
  );
}
